<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import { useRouter } from "vue-router";
import * as options from '@/utils/options';
import { ElMessage, ElMessageBox } from 'element-plus'
import { checkUserExist, createCustomer } from '@/api'

const router = useRouter();
// const toBoard = () => router.push("/");
const toBack = () => {
  if (window.history.length > 1) {
    router.back();
  } else {
    router.push('/');
  }
}
interface Manager {
  name: string,
  mail: string,
  phone: string
}
interface TaxInfo {
  taxInfoType: string,
  taxInfoDetail: string[]
}
interface CustomerForm {
  user: {
    userId: string,
    companyName: string
  },
  manager: Manager[],
  taxInfo: TaxInfo | null,
  meno: string
}

const mailType = ref<string>(null);
const mailDetail = ref<string>(null);

const form = reactive<CustomerForm>({
  user: {
    userId: '',
    companyName: ''
  },
  manager: [
    {
      name: '',
      mail: '',
      phone: ''
    }
  ],
  taxInfo: {
    taxInfoType: '',
    taxInfoDetail: []
  }
})

const mailTypePlaceholder = computed(() => {
  if (!mailType.value) return '발행 방식 선택'
  return mailType.value === 'EMAIL' ? '이메일(쉽표로 구분)' : '시스템';
})

const handleSearchAccount = async () => {
  if (form.user.userId.length < 1 ) {
    ElMessageBox.alert('검색어를 입력해주세요.', { confirmButtonText: '확인' });
    return
  }

  const params = {
    userId: form.user.userId
  }

  try {
    const response = await checkUserExist(params);
    if (response.success && response.result) {
      form.user.companyName = response.result.userName
    } else {
      ElMessageBox.alert('존재하지 않는 계정입니다.', { confirmButtonText: '확인' });
    }
  } catch (error) {
    if (error.message === 'Customer exist') {
      ElMessageBox.alert('이미 등록된 계정입니다.', { confirmButtonText: '확인' });
    } else {
      ElMessageBox.alert('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.<br/> 문제가 지속되면 관리자에게 문의하세요.', { 
        confirmButtonText: '확인',
        dangerouslyUseHTMLString: true
      });
    }
  }
}

const mainManagerNum = ref<number>(0)

const companyInputRef = ref<InputRef>(null)
const managerNameRef = ref<InputRef>(null)
const mailsInsertRef = ref<InputRef>(null)


const handleAddManager = () => {
  form.manager.push({
    name: '',
    mail: '',
    phone: '',
  })
}

const handleRemoveManager = (index) => {
  form.manager.splice(index, 1);
}

const setMainManager = (index) => {
  [form.manager[0], form.manager[index]] = [form.manager[index], form.manager[0]]
}

const handleSubmit = async () => {
  if (!validateFormData()) return
  const params = form

  params.manager.map(manager => {
    manager.phone = manager.phone.replace(/-/g, "")
    return manager
  })

  if (!mailType.value) {
    params.taxInfo = null;
  } else {
    params.taxInfo.taxInfoType = mailType.value;
    params.taxInfo.taxInfoDetail = mailDetail.value.split(',').filter(t => t !== '').map(t => t.trim());
    
    if (params.taxInfo.taxInfoType === 'EMAIL' && !params.taxInfo.taxInfoDetail.every(mail => isEmailText(mail))) {
      return showAlertAndFocus('유효하지 않은 이메일이 포함되어있습니다.', mailsInsertRef.value);
    }
    if (params.taxInfo.taxInfoType === 'SYSTEM' && params.taxInfo.taxInfoDetail.some(mail => isEmailText(mail))) {
      return showAlertAndFocus('시스템명이 이메일 형식으로 되어 있습니다.', mailsInsertRef.value);
    }
  } 
  
  // if (mailDetail.value) {
  //   params.taxInfo.taxInfoDetail = mailDetail.value.split(',').filter(t => t !== '').map(t => t.trim())
  // }

  try {
    const response = await createCustomer(params);
    console.log('고객 정보를 생성 성공:', response);
    ElMessageBox.alert('고객 정보를 생성했습니다. 고객 관리 목록으로 이동합니다.', {
      confirmButtonText: '확인',
      callback: () => {
        router.push({name: 'CustomerManage'});
      },
    });
  } catch (error) {
    console.error('Error Message:', error)
    ElMessage.error('고객 정보를 생성에 실패했습니다. 다시 시도해주세요')
  }
}

type InputRef = {
  focus: () => void;
  value?: string | number;
};


const validateFormData = () => {

  if (!form.user.companyName) return showAlertAndFocus('회사명을 입력해주세요.', companyInputRef.value);
  if (form.manager.length === 1 && !form.manager[0].name ) return showAlertAndFocus('담당자를 입력해주세요.', managerNameRef.value);
  const checkManager = form.manager.every(item => {
    return item.name && item.mail
  })
  if (!checkManager) return showAlertAndFocus('담당자 이름, 이메일은 필수 입력입니다.', managerNameRef.value);
  if (mailType.value && !mailDetail.value) return showAlertAndFocus('서류 발행 이메일을 입력하세요.', mailsInsertRef.value);
  return true
}

const isEmailText = (mail) => {
  const regEmail = /^[!#$%&'*+\-/=?^_`{|}~.a-zA-Z0-9-]+@[\w.-]+\.[A-Za-z0-9-]+$/;
  return regEmail.test(mail);
};


const showAlertAndFocus = (message: string, inputRef: InputRef | undefined) => {
  ElMessageBox.alert(message, { confirmButtonText: '확인' });
  if (inputRef && inputRef.focus) inputRef.focus();
  return false;
};

</script>

<template>
  <div class="page-add">
    <header class="page-header">
      <h2 class="tit-h2">
        고객 추가
      </h2>
      <div class="btn-group">
        <el-button
          type="primary"
          size="large"
          aria-label="이전으로"
          class="btn-icon"
          @click="toBack"
        >
          <font-awesome-icon icon="fa-solid fa-arrow-left-long" />
        </el-button>
      </div>
    </header>
    <el-form
      :model="form"
      label-position="left"
      label-width="100px"
      class="content-box"
    >
      <section class="section">
        <div class="section-header">
          <h3 class="tit-h3">
            기본 정보
          </h3>
          <div class="description">
            뷰저블 계정이 없어도 추가할 수 있으며, 회사명은 필수입니다.
          </div>
        </div>
        <el-form-item label="뷰저블 계정">
          <el-input
            v-model="form.user.userId"
            size="large"
            style="width: 300px"
            @keyup.enter="handleSearchAccount"
          >
            <template #append>
              <el-button
                aria-label="검색"
                @click="handleSearchAccount"
              >
                <font-awesome-icon icon="fa-solid fa-magnifying-glass" />
              </el-button>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item 
          label="회사명" 
          props="companyName"
          :rules="[
            { required: true, message: '회사명은 필수값입니다.' },
          ]"
        >
          <el-input
            v-model="form.user.companyName"
            size="large"
            style="width: 300px"
            ref="companyInputRef"
          ></el-input>
        </el-form-item>
      </section>
      <section class="section">
        <div class="section-header">
          <h3 class="tit-h3">
            담당자 정보
          </h3>
          <div class="description">
            최소 1명의 이름과 이메일은 필수입니다. 별도 이메일 계정이 없는 경우, 뷰저블 계정을 기재해주세요.
          </div>
        </div>
        <div class="form-item">
          <el-row 
            class="document-row"
            v-for="(manager, i) in form.manager"
            :key="`manager-${i}`"
          >
            <el-col 
              :span="2" 
              align="center"
            >
              <el-tooltip 
                v-if="i > 0" 
                content="대표로 지정"
                placement="top"
              >
                <el-button
                  icon
                  circle
                  size="small"
                  @click="setMainManager(i)"
                >
                  <font-awesome-icon  icon="fa-solid fa-crown"/>
                </el-button>
              </el-tooltip>
              <el-button 
                v-else
                text 
              >
                <el-text tag="b">
                  대표
                </el-text>
              </el-button>
            </el-col>
            <el-col :span="4">
              <el-input
                v-model="manager.name"
                size="large"
                placeholder="이름(필수)"
                ref="managerNameRef"
              ></el-input>
            </el-col>
            <el-col :span="6">
              <el-input
                v-model="manager.mail"
                size="large"
                placeholder="이메일(필수)"
              ></el-input>
            </el-col>
            <el-col :span="6">
              <el-input
                v-model="manager.phone"
                size="large"
                placeholder="핸드폰 번호(번호만 입력)"
              ></el-input>
            </el-col>
            <el-button
              v-if="i>0"
              type="primary"
              text
              aria-label="삭제"
              @click="handleRemoveManager(i)"
            >
              <font-awesome-icon icon="fa-regular fa-trash-can" />
            </el-button>
          </el-row>
          <el-button
            type="primary"
            plain
            @click="handleAddManager"
          > 
            <el-icon class="el-icon--left">
              <font-awesome-icon icon="fa-solid fa-plus" />
            </el-icon>
            추가
          </el-button>
        </div>
      </section>
      <section class="section">
        <div class="section-header">
          <h3 class="tit-h3">
            세금 계산서 발행 정보
          </h3>
          <div class="description">
            결제 방식이 무통장인 경우에만 입력해주세요.
          </div>
        </div>
        <div class="form-item">
          <el-row  class="document-row">
            <el-col :span="4">
              <el-select
                v-model="mailType"
                placeholder="발행 방식 선택"
                size="large"
              >
                <el-option
                  v-for="item in options.optionsTaxInfoType"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-col>
            <el-col :span="15">
              <el-input
                v-model="mailDetail"
                size="large"
                :placeholder="mailTypePlaceholder"
                :disabled="!mailType"
                ref="mailsInsertRef"
              ></el-input>
            </el-col>
          </el-row>
        </div>
      </section>
      <section class="section">
        <h3 class="tit-h3">
          메모
        </h3>
        <div class="form-item">
          <el-input
            v-model="form.memo"
            :rows="5"
            type="textarea"
            placeholder="입력하세요"
          />
        </div>
      </section>
    </el-form>
    <div class="btn-group">
      <el-button
        type="primary"
        size="large"
        @click="handleSubmit"
      >
        저장
      </el-button>
      <el-button
        type="default"
        size="large"
        @click="router.back()"
      >
        취소
      </el-button>
    </div>
  </div>
</template>

<style lang="scss">
.section {
  .section-header {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 24px;
    h3.tit-h3 {
      margin: 0;
    }
    .description {
      font-size: 11px;
    }
  }
}
</style>